import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FaMapMarkedAlt, 
  FaDrawPolygon, 
  FaFileAlt, 
  FaMoneyBillWave, 
  FaSitemap, 
  FaVideo, 
  FaShieldAlt, 
  FaBell,
  FaRobot,
  FaUsers,
  FaExchangeAlt,
  FaUserShield
} from 'react-icons/fa';
import './Features.css';

const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: FaRobot,
      title: t('features.watchman.title'),
      description: t('features.watchman.description'),
      color: '#65B152',
      benefits: ['🛰️ Satellite images every 15 days', '🤖 AI-powered change detection', '🚨 Instant alert notifications', '📊 Historical comparison timeline'],
      featured: true
    },
    {
      icon: FaMapMarkedAlt,
      title: t('features.property.title'),
      description: t('features.property.description'),
      color: '#4CAF50',
      benefits: ['Unlimited properties', 'Photo galleries', 'Location tracking', 'Custom tags']
    },
    {
      icon: FaDrawPolygon,
      title: t('features.map.title'),
      description: t('features.map.description'),
      color: '#2196F3',
      benefits: ['Satellite view', 'GPS boundaries', 'Multiple map layers', 'Export data']
    },
    {
      icon: FaDrawPolygon,
      title: t('features.boundary.title'),
      description: t('features.boundary.description'),
      color: '#FF9800',
      benefits: ['GPS accuracy', 'Area calculation', 'Save coordinates', 'Share boundaries']
    },
    {
      icon: FaMoneyBillWave,
      title: t('features.tax.title'),
      description: t('features.tax.description'),
      color: '#F44336',
      benefits: ['Smart reminders', 'Payment history', 'Multi-year tracking', 'Receipt storage']
    },
    {
      icon: FaUsers,
      title: t('features.neighbors.title'),
      description: t('features.neighbors.description'),
      color: '#00BCD4',
      benefits: ['Neighbor database', 'Contact information', 'Boundary records', 'Dispute history']
    },
    {
      icon: FaExchangeAlt,
      title: t('features.transfer.title'),
      description: t('features.transfer.description'),
      color: '#FF5722',
      benefits: ['Digital transfer', 'Document sharing', 'Ownership tracking', 'Transfer history']
    },
    {
      icon: FaUserShield,
      title: t('features.familyAccess.title'),
      description: t('features.familyAccess.description'),
      color: '#673AB7',
      benefits: ['Role-based access', 'Owner/Editor/Viewer', 'Permission control', 'Activity logs']
    },
    {
      icon: FaSitemap,
      title: t('features.familyTree.title'),
      description: t('features.familyTree.description'),
      color: '#9C27B0',
      benefits: ['Visual tree', 'Ownership history', 'Inheritance planning', 'Family notes']
    },
    {
      icon: FaFileAlt,
      title: t('features.documents.title'),
      description: t('features.documents.description'),
      color: '#795548',
      benefits: ['Cloud storage', 'Smart search', 'Quick access', 'Organized folders']
    },
    {
      icon: FaVideo,
      title: t('features.video.title'),
      description: t('features.video.description'),
      color: '#FFC107',
      benefits: ['Step-by-step guides', 'Multi-language', 'Expert tips', 'Regular updates']
    },
    {
      icon: FaShieldAlt,
      title: t('features.secure.title'),
      description: t('features.secure.description'),
      color: '#607D8B',
      benefits: ['End-to-end encryption', 'PIN protection', 'Secure backup', 'Privacy first']
    }
  ];

  return (
    <div className="features-page">
      {/* Hero Section */}
      <section className="features-hero" data-aos="fade-up">
        <div className="container">
          <h1>{t('features.title')}</h1>
          <p className="features-subtitle">{t('features.subtitle')}</p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-content">
        <div className="container">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={index} 
                className={`feature-row ${isEven ? 'row-normal' : 'row-reverse'} ${feature.featured ? 'featured-row' : ''}`}
                data-aos={isEven ? 'fade-right' : 'fade-left'}
              >
                {feature.featured && (
                  <div className="featured-badge">
                    <span className="badge-pulse"></span>
                    ⭐ FEATURED: AI-POWERED
                  </div>
                )}
                <div className="feature-content">
                  <div className="feature-icon-wrapper" style={{ '--feature-color': feature.color }}>
                    <Icon className="feature-icon-large" />
                  </div>
                  <h2>{feature.title}</h2>
                  <p className="feature-desc">{feature.description}</p>
                  <ul className="benefits-list">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i}>
                        <span className="check-icon">✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="feature-visual">
                  <div className="visual-card" style={{ borderColor: feature.color }}>
                    <Icon style={{ color: feature.color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>Perfect For Everyone</h2>
            <p>See how Land Tracker helps different users</p>
          </div>
          <div className="use-cases-grid">
            <div className="use-case-card" data-aos="fade-up" data-aos-delay="0">
              <div className="use-case-icon">👨‍🌾</div>
              <h3>Farmers</h3>
              <p>Manage multiple farms, track boundaries, and never miss tax deadlines</p>
            </div>
            <div className="use-case-card" data-aos="fade-up" data-aos-delay="100">
              <div className="use-case-icon">💼</div>
              <h3>Investors</h3>
              <p>Portfolio tracking, document organization, and quick property insights</p>
            </div>
            <div className="use-case-card" data-aos="fade-up" data-aos-delay="200">
              <div className="use-case-icon">👨‍👩‍👧‍👦</div>
              <h3>Families</h3>
              <p>Preserve heritage, plan inheritance, and keep everyone informed</p>
            </div>
            <div className="use-case-card" data-aos="fade-up" data-aos-delay="300">
              <div className="use-case-icon">🏢</div>
              <h3>Businesses</h3>
              <p>Enterprise property management with team collaboration features</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <div className="cta-content">
            <h2>Experience All Features Today</h2>
            <p>Download Land Tracker and unlock powerful property management tools</p>
            <button className="btn btn-primary btn-lg">
              Get Started Free
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
